/**
 * @file fileImportProvider.ts
 * A host environment that replaces the standard file open/drop flow of
 * <as-file-input> with its own native dialogs and file conversion provides
 * one through the `fileImport` section of its host registration (see
 * features/host/host.ts; e.g. grabcadPlugin.ts). The component never imports
 * host-specific code.
 */

import { getHost, registerHost } from '../../host/host.ts';

/** A file produced by a provider, ready for app consumption. */
export interface ImportedFile {
  filename: string;
  filetype: string;
  data: ArrayBuffer | Blob;
}

/** Per-interaction context handed to provider calls. */
export interface FileImportContext {
  /** Read a provider-specific option attribute off the component (e.g.
   *  grabcad-stl-unit, multiple). */
  getAttribute(name: string): string | null;
  /** Called with conversion progress where the provider reports it — a stage
   *  string ('converting', 'reading') on current hosts, a number on legacy ones. */
  onProgress?: (progress: string | number) => void;
  /** File-count progress through the provider's own work: `done` files of
   *  `total`. Providers must call it once with done=0 the moment the user starts
   *  waiting on them — after any host dialog has closed — and again as each file
   *  finishes. The component uses the first call to enter its importing state,
   *  because until the flow resolves there is no file yet and nothing else knows
   *  an import is running. */
  onImportProgress?: (done: number, total: number) => void;
  /** Aborts the provider's work. The component passes this to a provider that
   *  declares `cancellable`, and fires it when the user cancels a slow import. */
  signal?: AbortSignal;
}

export interface FileImportProvider {
  /** Content kinds this provider claims (e.g. ['mesh']). The component routes an
   *  upload through the provider only when its category is listed here;
   *  uploads of any other kind fall back to the native file input. Omit to claim
   *  every kind (the legacy behaviour). Distinct from the export-schema
   *  FileCategory — this is a coarse import-routing class, not a file role. */
  kinds?: readonly string[];
  /** True when the provider honours `ctx.signal`. The component only offers the
   *  user a Cancel action on a slow import if the provider says it can act on
   *  one — a Cancel button that does nothing is worse than none. */
  cancellable?: boolean;
  /** Overrides <as-import-group>'s "Recommended format: …" hint when this
   *  provider's env is active and the host hasn't set its own hint-default-text.
   *  The native file input's accept/filetype attribute (and its own "Accepts:"
   *  label) describes what the *browser* input takes — meaningless here since
   *  pickFile/claimDrop replace that input outright, so the provider states
   *  what its own dialog actually accepts instead. */
  acceptHintText?: string;
  /** URL for a real, connected anchor rendered right after acceptHintText —
   *  e.g. linking out to the host's own supported-format list rather than
   *  duplicating (and inevitably drifting from) it as static text. Must
   *  render as a genuine in-DOM `<a target="_blank">`, not a scripted
   *  window.open: some host webviews (GrabCAD's) block scripted opens
   *  entirely and only honor a real anchor the user clicks (see as-header.ts's
   *  _updateHelpLink for the same constraint). Ignored if acceptHintText is
   *  unset. */
  acceptHintUrl?: string;
  /** Link text for acceptHintUrl. Default: "Click here". */
  acceptHintLinkText?: string;
  /** Replaces the native file picker. Resolves the imported file(s) — several
   *  only when the component allows it (`multiple` attribute, readable off the
   *  context) — or null when the dialog is dismissed or the conversion is
   *  aborted. */
  pickFile(ctx: FileImportContext): Promise<ImportedFile[] | null>;
  /** Synchronously decide whether to claim dropped files (e.g. to convert
   *  them). Return the flow to run, or null to let the normal drop flow
   *  proceed. The flow is invoked only once the component is free to run it. */
  claimDrop(blobs: Blob[], ctx: FileImportContext): (() => Promise<ImportedFile[] | null>) | null;
}

/** Same as `registerHost(env, { fileImport: provider })`. */
export function registerFileImportProvider(env: string, provider: FileImportProvider): void {
  registerHost(env, { fileImport: provider });
}

export function getFileImportProvider(env: string | undefined | null): FileImportProvider | null {
  return getHost(env)?.fileImport ?? null;
}

/**
 * Like getFileImportProvider, but only returns the provider when it claims the
 * given content kind. A provider with no declared `kinds` claims all kinds.
 * Used by <as-file-input> so that, e.g., a mesh-only host provider (GrabCAD)
 * handles mesh uploads while image/text/data uploads fall through to the native
 * file input.
 */
export function getFileImportProviderForKind(
  env: string | undefined | null,
  kind: string,
): FileImportProvider | null {
  const provider = getFileImportProvider(env);
  if (!provider) return null;
  if (provider.kinds && !provider.kinds.includes(kind)) return null;
  return provider;
}
